/**
 * Dashboard route — visualizaciones agregadas de membresía trimestral.
 *
 * Per FWA-PWA-032 G0 D6. Datos agregados sin PII individual.
 */
import { useEffect, useMemo, useState } from 'react';
import { BarChartRama } from '../../components/dashboard/BarChartRama.js';
import { KpiCard } from '../../components/dashboard/KpiCard.js';
import { PieChartEstatus } from '../../components/dashboard/PieChartEstatus.js';
import { Button } from '../../components/ui/Button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/Card.js';
import { useAuth } from '../../contexts/AuthContext.js';
import { FwaApiError } from '../../lib/api.js';
import {
  useBackfillMembresia,
  useMembresiaByQuarter,
  useMembresiaList,
} from '../../lib/queries/membresia.js';

function computeCurrentQuarter(): string {
  const now = new Date();
  const month = now.getUTCMonth() + 1;
  const q = Math.ceil(month / 3);
  return `${now.getUTCFullYear()}-Q${q}`;
}

export function Dashboard() {
  const { state } = useAuth();
  const isAdmin =
    state.status === 'authenticated' &&
    (state.user.rol === 'admin-distrital' || state.user.rol === 'admin-nacional');

  const list = useMembresiaList();
  const quarters = useMemo(() => {
    if (!list.data) return [];
    return list.data.data.data.map((m) => m.quarter).sort((a, b) => b.localeCompare(a)); // most recent first
  }, [list.data]);

  const [selectedQuarter, setSelectedQuarter] = useState<string>('');

  useEffect(() => {
    if (!selectedQuarter && quarters.length > 0 && quarters[0]) {
      setSelectedQuarter(quarters[0]);
    }
  }, [quarters, selectedQuarter]);

  const detail = useMembresiaByQuarter(selectedQuarter || undefined);
  const backfill = useBackfillMembresia();
  const [backfillError, setBackfillError] = useState<string>('');
  const [showBackfillConfirm, setShowBackfillConfirm] = useState(false);

  const handleBackfill = () => {
    const target = selectedQuarter || computeCurrentQuarter();
    setBackfillError('');
    backfill.mutate(
      { quarter: target },
      {
        onSuccess: () => {
          setShowBackfillConfirm(false);
          setSelectedQuarter(target);
        },
        onError: (err) => {
          if (err instanceof FwaApiError) {
            setBackfillError(err.problem.detail ?? err.problem.title);
          } else {
            setBackfillError(err.message);
          }
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard distrital</h1>
        {isAdmin && (
          <Button
            variant="primary"
            onClick={() => setShowBackfillConfirm(true)}
            disabled={backfill.isPending}
          >
            {backfill.isPending ? 'Ejecutando…' : 'Backfill quarter'}
          </Button>
        )}
      </div>

      {backfillError && (
        <div
          className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700"
          role="alert"
        >
          {backfillError}
        </div>
      )}

      {list.isLoading && <div className="text-center py-8 text-gray-600">Cargando snapshots…</div>}
      {list.error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700" role="alert">
          Error cargando snapshots: {list.error.message}
        </div>
      )}

      {list.data && quarters.length === 0 && (
        <Card>
          <CardBody>
            <p className="text-gray-600 py-4">
              Sin snapshots de membresía disponibles.{' '}
              {isAdmin ? (
                <span>
                  Ejecuta <strong>Backfill quarter</strong> para generar el primero.
                </span>
              ) : (
                <span>Solicita al admin distrital que ejecute backfill.</span>
              )}
            </p>
          </CardBody>
        </Card>
      )}

      {quarters.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Quarter</CardTitle>
          </CardHeader>
          <CardBody>
            <label className="flex flex-col gap-1 max-w-xs">
              <span className="text-sm font-medium text-gray-700">Selecciona quarter</span>
              <select
                className="px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-distrito-500"
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
              >
                {quarters.map((q) => (
                  <option key={q} value={q}>
                    {q}
                  </option>
                ))}
              </select>
            </label>
          </CardBody>
        </Card>
      )}

      {detail.isLoading && selectedQuarter && (
        <div className="text-center py-8 text-gray-600">Cargando snapshot {selectedQuarter}…</div>
      )}

      {detail.data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiCard label="Total niñas" value={detail.data.data.total_ninas} tone="blue" />
            <KpiCard label="Activas" value={detail.data.data.total_activas} tone="green" />
            <KpiCard
              label="Altas del trimestre"
              value={detail.data.data.total_altas_trimestre}
              tone="green"
              hint={`Quarter ${detail.data.data.quarter}`}
            />
            <KpiCard
              label="Bajas del trimestre"
              value={detail.data.data.total_bajas_trimestre}
              tone="red"
              hint={`Quarter ${detail.data.data.quarter}`}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Distribución por rama</CardTitle>
              </CardHeader>
              <CardBody>
                <BarChartRama counts={detail.data.data.por_rama_json} />
              </CardBody>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Distribución por estatus</CardTitle>
              </CardHeader>
              <CardBody>
                <PieChartEstatus counts={detail.data.data.por_estatus_json} />
              </CardBody>
            </Card>
          </div>
        </>
      )}

      {showBackfillConfirm && (
        <dialog
          open
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 max-w-none w-full h-full"
          aria-labelledby="backfill-title"
        >
          <Card className="max-w-md mx-4">
            <CardHeader>
              <CardTitle>
                <span id="backfill-title">Confirmar backfill</span>
              </CardTitle>
            </CardHeader>
            <CardBody>
              <p className="text-sm text-gray-600">
                Ejecutar backfill para quarter{' '}
                <strong>{selectedQuarter || computeCurrentQuarter()}</strong>. Esto leerá el padrón
                de niñas actual y generará una nueva versión del snapshot.
              </p>
              <div className="flex gap-3 mt-4">
                <Button variant="primary" onClick={handleBackfill} disabled={backfill.isPending}>
                  {backfill.isPending ? 'Ejecutando…' : 'Confirmar'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowBackfillConfirm(false)}
                  disabled={backfill.isPending}
                >
                  Cancelar
                </Button>
              </div>
            </CardBody>
          </Card>
        </dialog>
      )}
    </div>
  );
}
